/**
 * The creator wallet of a Fansly account (fansly.com/creator/wallet): balances,
 * sales (earnings transactions), monthly statements, payout requests and
 * methods, and whether Fansly still needs payout documents. Readers follow
 * Fansly's own client (bundle chunk 189 + main). Money is thousandths.
 */
import { accountsIn, asList, asObj, catalogOf, num, str, toMs, unwrap, type FanslyAccount, type MediaCatalog, type Raw } from '../data'
import { productKey } from '../statistics/statsData'

// ── Balances ───────────────────────────────────────────────────────────────

/** Fansly's wallet types: 1 = main (spending) wallet, 2 = earnings wallet. */
export const EARNINGS_WALLET = 2
const BLOCK_OUTGOING = 2

export interface Balances {
  /** Earnings wallet balance: available for payout (mills); null when unknown. */
  available: number | null
  /** Earnings still pending (unlocked on a rolling 7 days). */
  pending: number | null
  /** Payouts are blocked on the earnings wallet. */
  blocked: boolean
}

export function balancesOf(wallets: unknown, earnings: unknown): Balances {
  const list = asList(unwrap(wallets))
  const w = list.find((x) => num(x.type) === EARNINGS_WALLET)
  const e = asObj(unwrap(earnings))
  return {
    available: w ? num(w.balance64 ?? w.balance) : null,
    pending: 'pendingBalance' in e ? num(e.pendingBalance) : null,
    blocked: w ? (num(w.flags) & BLOCK_OUTGOING) === BLOCK_OUTGOING : false,
  }
}

// ── Sales (earnings transactions) ──────────────────────────────────────────

export type TxStatus = 'pending' | 'available' | 'other'

export interface Tx {
  id: string
  type: number
  /** Product key (statistics product.* labels). */
  product: string
  /** What the fan paid (mills). */
  gross: number
  /** Fansly's fee: gross − net (mills). */
  fee: number
  /** What reached the wallet (mills). */
  net: number
  fanId: string
  correlationId: string
  createdAt: number
  status: TxStatus
  raw: Raw
}

/**
 * What the fan paid, from what reached the wallet. Fansly's sale rows carry the
 * NET in both `amount` and `destinationAmount`; `destinationTax` is the fee
 * rate in hundredths of a percent (2000 = 20%), not money. Verified against
 * Fansly's own stats gross (e.g. net 31192 at 2000 → gross 38990).
 */
export function grossOf(net: number, feeRate: number): number {
  return feeRate > 0 && feeRate < 10000 ? Math.round((net * 10000) / (10000 - feeRate)) : net
}

export function txOf(raw: Raw): Tx {
  const net = raw.destinationAmount != null ? num(raw.destinationAmount) : num(raw.amount)
  const gross = grossOf(net, num(raw.destinationTax))
  const s = num(raw.status)
  return {
    id: str(raw.transactionId) || str(raw.id),
    type: num(raw.type),
    product: productKey(raw.type),
    gross,
    fee: gross - net,
    net,
    fanId: str(raw.correlationAccountId) || str(raw.senderId),
    correlationId: str(raw.correlationId),
    createdAt: toMs(raw.createdAt),
    status: s === 1 ? 'pending' : s === 2 ? 'available' : 'other',
    raw,
  }
}

export interface TxPage {
  total: number
  rows: Tx[]
  accounts: Map<string, FanslyAccount>
  catalog: MediaCatalog
}

export function txPageOf(payload: unknown): TxPage {
  const o = asObj(unwrap(payload))
  return {
    total: num(o.total),
    rows: asList(o.data).map(txOf).filter((r) => r.id),
    accounts: accountsIn(payload),
    catalog: catalogOf(payload),
  }
}

/** Pages of sales joined into one list (newer pages repeat nothing). */
export function mergeTx(a: TxPage, b: TxPage): TxPage {
  const seen = new Set(a.rows.map((r) => r.id))
  return {
    total: b.total || a.total,
    rows: [...a.rows, ...b.rows.filter((r) => !seen.has(r.id))],
    accounts: new Map([...a.accounts, ...b.accounts]),
    catalog: { media: new Map([...a.catalog.media, ...b.catalog.media]), bundles: new Map([...a.catalog.bundles, ...b.catalog.bundles]) },
  }
}

export const TX_PAGE = 25

// ── Monthly statements ─────────────────────────────────────────────────────

export interface Month {
  year: number
  /** 1–12. */
  month: number
  /** UTC first day of the month. */
  bucket: number
  gross: number
  net: number
  /** "Top x% of creators" (Fansly shows it at 5% or better, or when net > $1). */
  topPercent: number
}

export function monthsOf(payload: unknown): Month[] {
  return asList(unwrap(payload))
    .map((r) => {
      const year = num(r.year)
      const month = num(r.month)
      return { year, month, bucket: year && month ? Date.UTC(year, month - 1, 1) : 0, gross: num(r.totalGross), net: num(r.totalNet), topPercent: num(r.topPercent) }
    })
    .filter((m) => m.bucket > 0)
    .sort((a, b) => b.bucket - a.bucket)
}

export function showTop(m: Month): boolean {
  return m.topPercent > 0 && (m.topPercent <= 5 || (m.topPercent <= 100 && m.net > 1000))
}

/** The query Fansly's wallet sends for statements: this and last calendar year, to now. */
export function monthsQuery(now = Date.now()): { before: number; after: number } {
  return { before: now, after: Date.UTC(new Date(now).getUTCFullYear() - 1, 0, 1) }
}

// ── Payouts ────────────────────────────────────────────────────────────────

/** Fansly's PAYOUT_REQUEST_STATUS. */
export const PAYOUT_STATUS: Record<number, string> = {
  1: 'created',
  2: 'requested',
  3: 'approved',
  4: 'canceled',
  5: 'refunded',
  6: 'error',
  7: 'processing',
  8: 'processed',
  10: 'processing',
}

export interface PayoutRequest {
  id: string
  amount: number
  status: string
  methodId: string
  createdAt: number
}

export function payoutsOf(payload: unknown): { total: number; rows: PayoutRequest[] } {
  const o = asObj(unwrap(payload))
  const list = Array.isArray(o.data) ? asList(o.data) : asList(unwrap(payload))
  return {
    total: num(o.total),
    rows: list.map((r) => ({
      id: str(r.id),
      amount: num(r.amount),
      status: PAYOUT_STATUS[num(r.status)] ?? 'unknown',
      methodId: str(r.payoutMethodId),
      createdAt: toMs(r.createdAt),
    })),
  }
}

/** Fansly's PAYOUT_METHOD_STATUS. */
export const METHOD_STATUS: Record<number, string> = { 1: 'idle', 2: 'requested', 3: 'approved', 4: 'canceled', 5: 'refunded' }

/** Fansly's payout providers by id (the icons its wallet shows). */
export function providerKey(providerId: string): string {
  const n = Number(providerId)
  if (n === 1) return 'skrill'
  if (n === 2) return 'paxum'
  if (n === 3 || n === 4 || n === 30) return 'bank'
  if (n === 5) return 'cosmo'
  if (n === 10) return 'pilot'
  if ((n >= 100 && n < 120) || n === 151) return 'crypto'
  if (n >= 120 && n < 200) return 'ewallet'
  return 'other'
}

export interface PayoutMethod {
  id: string
  providerId: string
  provider: string
  status: string
  isDefault: boolean
  /** The last digits Fansly shows ("ending in 1234"), when the method's metadata has them. */
  ending: string
  /** Smallest payout this method takes (mills; 0 = no minimum). */
  minPayout: number
  /** Fansly holds payouts to this method until tax information (DAC7, Canada, Australia) is supplied. */
  taxLocked: boolean
}

function metadataOf(v: unknown): Raw {
  if (typeof v !== 'string') return asObj(v)
  try {
    return asObj(JSON.parse(v))
  } catch {
    return {}
  }
}

export function methodsOf(payload: unknown): PayoutMethod[] {
  return asList(unwrap(payload)).map((r) => {
    const meta = metadataOf(r.metadata)
    const hint = str(meta.field1) || str(meta.field0)
    return {
      id: str(r.id),
      providerId: str(r.providerId),
      provider: providerKey(str(r.providerId)),
      status: METHOD_STATUS[num(r.status)] ?? 'unknown',
      isDefault: (num(r.flags) & 2) === 2,
      ending: hint.replace(/\s+/g, '').slice(-4),
      minPayout: num(meta.minPayoutAmount),
      taxLocked: !!(meta.dac7_locked || meta.caTax_locked || meta.auTax_locked),
    }
  })
}

/**
 * What Fansly's home banner says about payout documents (types 1 and 2):
 * none on file → "missing"; only denied ones (status 4) and none approved
 * (status 2) → "denied"; else nothing to say.
 */
export function documentationState(payload: unknown): 'missing' | 'denied' | null {
  const list = asList(unwrap(payload))
  if (!list.length) return 'missing'
  const approved = list.some((d) => num(d.status) === 2)
  const denied = list.some((d) => num(d.status) === 4)
  return denied && !approved ? 'denied' : null
}
